/**
 * The contents of this file are subject to the license and copyright
 * detailed in the LICENSE_ATMIRE and NOTICE_ATMIRE files at the root of the source
 * tree and available online at
 *
 * https://www.atmire.com/software-license/
 */
import { PaginationComponentOptions } from '../../../app/shared/pagination/pagination-component-options.model';
import { SortOptions } from '../../../app/core/cache/models/sort-options.model';
import { Observable } from 'rxjs';
import { ListableObject } from '../../../app/shared/object-collection/shared/listable-object.model';
import { PaginatedList } from '../../../app/core/data/paginated-list.model';
import { FindListOptions } from '../../../app/core/data/request.models';
import { RESTURLCombiner } from '../../../app/core/url-combiner/rest-url-combiner';
import { HrefOnlyDataService } from '../../../app/core/data/href-only-data.service';
import { RemoteData } from '../../../app/core/data/remote-data';
import { AtmireHrefListableObjectSourceModel } from '../listable-object-sources';
import { AbstractListableObjectSource } from './abstract-listable-object-source';
import { CacheableObject } from '../../../app/core/cache/object-cache.reducer';
import { environment } from '../../../environments/environment';

/**
 * Retrieves objects via {@link HrefOnlyDataService}.
 */
export class AtmireHrefListableObjectSource<T extends ListableObject & CacheableObject> extends AbstractListableObjectSource<T> {
  private readonly href: string;

  constructor(
    model: AtmireHrefListableObjectSourceModel<T>,
    private hrefOnlyDataService: HrefOnlyDataService,
  ) {
    super(model);
    this.href = model.href;
  }

  public getList(
    paginationOptions: Partial<PaginationComponentOptions>, sort: Partial<SortOptions>
  ): Observable<RemoteData<PaginatedList<ListableObject>>> {
    return this.hrefOnlyDataService.findAllByHref<T>(
      this.normalizedHref,
      Object.assign(new FindListOptions(), {
        sort: sort,
        currentPage: paginationOptions.currentPage,
        elementsPerPage: paginationOptions.pageSize,
      }),
      this.useCachedVersionIfAvailable,
      this.reRequestOnStale,
      ...this.linksToFollow
    );
  }

  /**
   * In case the provided url is not a valid self url, this will add the base url to it
   */
  private get normalizedHref(): string {
    if (this.href.includes(environment.rest.baseUrl)) {
      return this.href;
    } else {
      return new RESTURLCombiner(...this.href.split('/')).toString();
    }
  }

  public get params(): object {
    return {href: this.href};
  }
}
